import React, { useEffect } from 'react'
import { Header } from '../components/layout/Header'
import { Footer } from '../components/layout/Footer'
import { EmailSignup } from '../components/EmailSignup'
import SEO from '../components/SEO'

const LandingPage: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <div className="min-h-screen bg-white font-sans text-fg-2">
      <SEO 
        title="Jobloads - Workforce Coordination & Attendance Reliability" 
        description="Jobloads simplifies coordination for high-dependency workforces. Launching Crewloads: a reliable transport network for verified attendance in New Zealand."
        canonicalPath="/"
      />
      <Header />

      {/* Hero */}
      <section className="hero">
        <div className="pattern pattern--teal opacity-[0.018]"></div>
        <div className="hero-inner">
          <div className="eyebrow">
            <span className="dot"></span>
            <span>Jobloads · workforce coordination</span>
          </div>
          <h1 className="mt-6 text-[clamp(42px,8vw,104px)] font-semibold leading-[0.98] tracking-[-0.04em] text-midnight-blue">
            We sell attendance reliability,<br />
            <span className="text-midnight-black">not rides.</span>
          </h1>
          <p className="lead mt-6 md:mt-9 mx-auto max-w-[720px] text-[20px] md:text-[22px] text-fg-3 font-subheading leading-[1.55]">
            Jobloads simplifies coordination for high-dependency workforces. Crewloads is our upcoming transport network that turns informal lift-sharing into a reliable way to get everyone to their shift on time.
          </p>
        </div>
      </section>

      {/* Crewloads Narrative */}
      <section className="section-crewloads" id="crewloads">
        <div className="pattern pattern--teal opacity-[0.012]"></div>
        <div className="crewloads-inner relative z-10">
          <div className="sec-label">
            <span className="num">01</span>
            <span className="rule"></span>
            <span>What we're building</span>
          </div>
          <h2>We're building Crewloads.</h2>
          <p>
            We're solving the transport gaps that cost employers money and frontline workers opportunities. By organising the cars already on the road, we're building a structured network that ensures shifts start on time.
          </p>
          <p className="mt-8">
            When a car breaks down or a ride falls through, the shift goes short-staffed. We believe the best solution isn't adding more transport; it's organising the transport that already exists.
          </p>
          <div className="mt-12 flex justify-center">
            <button 
              onClick={() => document.getElementById('stay-updated')?.scrollIntoView({ behavior: 'smooth' })}
              className="px-7 py-4 bg-midnight-black text-white font-semibold rounded-xl hover:bg-black transition-all shadow-sm"
            >
              Get notified when we launch
            </button>
          </div>
          <div className="crewloads-image mt-12 md:mt-[88px] max-w-[1040px] mx-auto rounded-[24px] overflow-hidden border border-stone shadow-sm">
            <img 
              src="/marketing/every-ride-counts-on-crewloads.jpg" 
              alt="Co-worker leaning out of a car window on the way to a shift" 
              className="w-full h-auto block saturate-[0.92]"
            />
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="story" id="story">
        <div className="max-w-[1200px] mx-auto px-8">
          <div className="story-head">
            <div className="sec-label">
              <span className="num">02</span>
              <span className="rule"></span>
              <span>Our story</span>
            </div>
            <h2>Built from the orchard up.</h2>
            <p className="mt-6 text-[20px] md:text-[22px] font-subheading text-fg-3 leading-[1.55] max-w-[740px]">
              Crewloads began in my Gisborne orchard. I was hiring four people for every one position, and while transport isn't the only barrier, it’s a huge one. We built this to close that gap and help shifts start on time.
            </p>
          </div>

          <div className="story-grid">
            <div className="story-body">
              <p>
                I've spent years running Jobloads, the business behind Crewloads. We created this because of a problem I felt every morning in horticulture: transport is the silent barrier that keeps workers from reaching the job.
              </p>
              <p>
                We built Crewloads because we knew someone was already making the trip. By matching people who need a ride with co-workers already on their way, we remove the friction that stands between people and their potential.
              </p>

              <div className="founder mt-10 p-8 bg-pebble border border-stone rounded-2xl flex gap-6 items-start">
                <img 
                  src="/marketing/candice-pardy.png" 
                  alt="Candice Pardy" 
                  className="w-20 h-20 md:w-[88px] md:h-[88px] rounded-full object-cover flex-shrink-0 grayscale-[0.7]"
                />
                <div>
                  <div className="who font-semibold text-xl text-midnight-black mb-2">Candice Pardy · Founder</div>
                  <p className="text-[19px] leading-relaxed text-fg-3">
                    I build systems that simplify coordination in industries that depend on trust. My background in retail, horticulture, and recruitment taught me that effective systems reduce friction rather than adding to it. Crewloads is our commitment to making sure everyone can get to the jobs they want.
                  </p>
                  <a 
                    className="inline-block mt-3 font-semibold text-[19px] text-midnight-black border-b border-midnight-black pb-0.5 hover:opacity-70 transition-opacity" 
                    href="https://candicepardy.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    candicepardy.com
                  </a>
                </div>
              </div>

              {/* Evolution timeline */}
              <div className="mt-12 md:mt-20 pt-12 md:pt-16 border-t border-stone">
                <div className="text-[19px] font-semibold text-fg-4 mb-8">The evolution</div>
                <div className="evo-row">
                  <div className="evo-num text-fg-5">01</div>
                  <div>
                    <div className="evo-name">Carloads</div>
                    <div className="evo-desc">Coordinating shared rides for orchard workers in Gisborne.</div>
                  </div>
                </div>
                <div className="evo-row">
                  <div className="evo-num text-fg-5">02</div>
                  <div>
                    <div className="evo-name">Jobloads</div>
                    <div className="evo-desc">Connecting seasonal workers with jobs across New Zealand.</div>
                  </div>
                </div>
                <div className="evo-row now">
                  <div className="evo-num text-midnight-blue">03</div>
                  <div>
                    <div className="evo-name">Crewloads</div>
                    <div className="evo-desc">Verified rides that remove transport barriers and improve attendance.</div>
                    <div className="evo-tag text-teal">Coming soon</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:sticky lg:top-[104px]">
              <div className="why bg-midnight-black text-white rounded-2xl p-8 md:p-12 relative overflow-hidden">
                <div className="pattern pattern--teal opacity-[0.04] bg-[length:240px]"></div>
                <div className="why-inner relative z-10">
                  <h3 className="text-2xl font-semibold mb-9 text-white">Why it matters</h3>
                  <div className="why-item flex gap-5 items-start mb-8">
                    <div className="why-dot flex-shrink-0 w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-teal pulse"></div>
                    </div>
                    <div>
                      <div className="why-h font-semibold text-xl mb-1.5 text-white">Attendance reliability</div>
                      <p className="why-p text-[19px] leading-relaxed text-white/60">Removing the transport barriers that keep rosters from being fully covered.</p>
                    </div>
                  </div>
                  <div className="why-item flex gap-5 items-start mb-8">
                    <div className="why-dot flex-shrink-0 w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-teal pulse"></div>
                    </div>
                    <div>
                      <div className="why-h font-semibold text-xl mb-1.5 text-white">Worker cost-sharing</div>
                      <p className="why-p text-[19px] leading-relaxed text-white/60">Helping co-workers share the ride and recover their fuel and running costs.</p>
                    </div>
                  </div>
                  <div className="why-item flex gap-5 items-start">
                    <div className="why-dot flex-shrink-0 w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-teal pulse"></div>
                    </div>
                    <div>
                      <div className="why-h font-semibold text-xl mb-1.5 text-white">Structured safety</div>
                      <p className="why-p text-[19px] leading-relaxed text-white/60">Turning informal arrangements into a structured, safe, and verified network.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA section */}
      <section className="cta" id="stay-updated">
        <div className="pattern pattern--teal opacity-[0.018]"></div>
        <div className="cta-inner relative z-10">
          <div className="cta-card bg-white border border-stone rounded-[24px] p-16 text-center">
            <div className="sec-label justify-center">
              <span className="num">03</span>
              <span className="rule"></span>
              <span>Stay updated</span>
            </div>
            <h2 className="text-[clamp(36px,4.5vw,52px)] font-semibold leading-[1.05] tracking-[-0.03em] text-midnight-blue">Join the journey.</h2>
            <p className="mt-5 mb-10 mx-auto max-w-[480px] font-subheading text-[19px] text-fg-3">
              Sign up for Jobloads updates and product announcements.
            </p>
            <div className="max-w-[440px] mx-auto">
              <EmailSignup productName="Jobloads" />
            </div>
            <div className="mt-4 text-[13px] text-fg-4">No spam. Operational updates only.</div>
          </div>
        </div>
      </section>

      {/* Investors */}
      <section className="investors">
        <div className="max-w-[1200px] mx-auto px-8">
          <div className="investors-label">Our investors</div>
          <div className="investors-row">
            <img 
              src="/investors/arc-angels.png" 
              alt="Arc Angels" 
              className="logo-arc-angels" 
            />
            <img 
              src="/investors/icehouse-ventures.webp" 
              alt="Icehouse Ventures" 
              className="logo-icehouse"
            />
            <img 
              src="/investors/k1w1.png" 
              alt="K1W1" 
              className="logo-k1w1" 
            />
            <img 
              src="/investors/startmate.png" 
              alt="Startmate" 
              className="logo-startmate"
            />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default LandingPage
